import React, {useState} from "react";
import {Card, CardContent, Grid, Typography, Box, Button} from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import {Done} from "@mui/icons-material";
import {PortTheme} from "~/ui/theme";
import {
    ShareAutoVisitButton,
    ShareAutoVisitButtonProps
} from "~/apps/packages/auto-visits/components/ShareAutoVisitButton";
import {FloatingActions} from "~/apps/core/ui/FloatingActions";
import {DoneFilled} from "~/ui/icons";
import {useGetAdmissionCar, useGetAdmissionEmployer} from "~/apps/packages/passes/hooks";
import {getLocationForCreateAdmission} from "~/apps/packages/passes/components/CreatePage";
import {useGlobalHistory} from "~/components/ApplicationHistory";
import moment from "moment";



export type AutoVisitCreateResultProps = {
    autoVisit: ShareAutoVisitButtonProps["autoVisit"],
    onContinue?: () => void
}


const useStyles = makeStyles<PortTheme, AutoVisitCreateResultProps>(theme => ({
    iconContainer: {
        width: 106,
        height: 106,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#FFFFFF",
        padding: 22,
        fontWeight: 700,
        backgroundColor: theme.palette.additionalPrimary.main,
        backgroundClip: "padding-box",
        borderRadius: 106,
        borderStyle: "solid",
        borderColor: "#009ACF19",
        borderWidth: 15,
        "& .MuiSvgIcon-root": {
            width: 40,
            height: 40,
            '@media (max-height: 640px)': {
                width: 21,
                height: 21
            }
        },
        '@media (max-height: 640px)': {
            width: 56,
            height: 56,
            borderWidth: 8,
        }
    },
    content: {
        paddingTop: "78px !important",
        paddingBottom: "78px !important",
        textAlign: "center",
        '@media (max-height: 640px)': {
            paddingTop: "15px !important",
            paddingBottom: "15px !important",
        }
    },
    cardTitle: {
        marginTop: 32,
        '@media (max-height: 640px)': {
            marginTop: 4,
        }
    },
    cardDescription: {
        marginTop: '4px',
        paddingLeft: 20,
        paddingRight: 20,
    }
}));


export const AutoVisitCreateResult = (props: AutoVisitCreateResultProps) => {
    const classes = useStyles(props);
    const getEmployerForAdmission = useGetAdmissionEmployer();
    const getCarForAdmission = useGetAdmissionCar();
    const history = useGlobalHistory();
    const {autoVisit, onContinue} = props;
    const [loading, setLoading] = useState(false);

    const handleNextClick = () => {
        onContinue && onContinue();
    }

    const handleCreateAdmissionClick = async () => {
        setLoading(true);
        try {
            const employer = await getEmployerForAdmission(autoVisit.Driver.Name);
            const car = await getCarForAdmission(autoVisit.Vehicle.ID.replace(/[./\\!@#$%^&*()_+,:'"`~]/g, ''));
            const location = getLocationForCreateAdmission({
                fullName: autoVisit.Driver.Name,
                ...employer,
                carType: car?.type ? Number(car.type) : null,
                carBrand: car?.brand,
                carColor: car?.color,
                carModel: car?.model,
                carRegNumber: car?.regNumber || autoVisit.Vehicle.ID,
                passDate: autoVisit.Date,
            }, []);
            location.pathname = "/apps/passes" + location.pathname;
            history.replace(location);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Card>
                <CardContent className={classes.content}>
                    <Grid container direction="column" alignItems="center">
                        <Grid item>
                            <div className={classes.iconContainer}>
                                <Done color="inherit"/>
                            </div>
                        </Grid>
                        <Grid item className={classes.cardTitle}>
                            <Typography variant="screenTitle" color="primary">
                                Автовизит успешно создан
                            </Typography>
                        </Grid>
                        <Grid item className={classes.cardDescription}>
                            <Typography variant="screenText" color="secondary">
                                Ожидайте согласования автовизита
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Box mt={2}>
                <ShareAutoVisitButton autoVisit={autoVisit}/>
            </Box>

            <Box minHeight={80} height={140}/>
            <FloatingActions>
                <Button fullWidth onClick={handleCreateAdmissionClick} color="secondary" disabled={loading}>
                    Создать заявку на пропуск
                </Button>
                <Button fullWidth onClick={handleNextClick} startIcon={<DoneFilled/>}>
                    Готово
                </Button>
            </FloatingActions>
        </>
    )
}
